import { useReactiveVar } from '@apollo/client';
import { Theme } from '@mui/material';
import { Door } from 'graphql/generated/graphqlTypes';
import useBatteryStatusUI from 'hooks/useBatteryStatusUI/useBatteryStatusUI';
import { Translation } from 'hooks/useTranslation/useTranslation';
import { orderBy } from 'lodash';
import { useCallback, useState } from 'react';
import { doorsWithLocationsVar } from 'state/vars';

interface Sorting {
  isAscending?: boolean;
  columnName?: string;
}

const useSortingLocks = (translation: Translation, theme: Theme) => {
  const doorsWithLocations = useReactiveVar(doorsWithLocationsVar);
  const [sorting, setSorting] = useState<Sorting>({ isAscending: true, columnName: translation.name });
  const { getBatteryData } = useBatteryStatusUI(translation);

  const handleSorting = useCallback(
    (item: Door, columnName?: string) => {
      switch (columnName) {
        case translation.serial_number:
          return item.hardwareIdentifier;
        case translation.battery_status:
          return getBatteryData(item.batteryWarningLevel, theme).sortNumber;
        case translation.office_mode:
          return item.officeMode.weekDays.some((day) => day !== null)
            ? // eslint-disable-next-line no-magic-numbers
              item.officeMode.weekDays[0].from.slice(0, -3)
            : '';
        case translation.name:
        default:
          return item.name;
      }
    },
    [getBatteryData, translation.battery_status, translation.name, translation.office_mode, translation.serial_number]
  );

  const handleSortingOnClick = useCallback(
    (columnName: string) => {
      if (sorting.isAscending !== undefined && sorting.columnName === columnName) {
        return setSorting({ isAscending: !sorting.isAscending, columnName });
      }
      return setSorting({ isAscending: true, columnName });
    },
    [sorting.columnName, sorting.isAscending]
  );

  const handleSortingIcon = (columnName: string) => {
    if (sorting.isAscending !== undefined)
      if (sorting.columnName === columnName) return sorting.isAscending ? 'ArrowUpSorting' : 'ArrowDownSorting';
    return undefined;
  };

  const getSortedArray = useCallback(
    () =>
      orderBy(doorsWithLocations, (item) => handleSorting(item, sorting.columnName), [
        sorting.isAscending ? 'asc' : 'desc'
      ]),
    [doorsWithLocations, handleSorting, sorting.columnName, sorting.isAscending]
  );

  return { handleSortingOnClick, handleSortingIcon, getSortedArray };
};

export default useSortingLocks;
